// Design system primitives (RK-SYS-005 section 2): Royal Kalahari palette (BD-79), Inter for all text,
// body with tabular figures, 14 px cards, 999 px chips, calm 150-200 ms motion, 44 px targets.
import { NatureScene, RkIcon } from "../brand/Brand";
import { forwardRef, type ButtonHTMLAttributes, type ForwardedRef, type ReactElement, type Ref, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { Link, useNavigate, type LinkProps } from "react-router-dom";
import { ArrowLeft, Loader2, RefreshCw, Search } from "lucide-react";
import { type ChipDef, type Tone, READ_FAILED_RETRY, formatRand, nextStepFor, readFailedMessage, readFailedRetryLabel } from "@rk/shared";
import { useOnline } from "../offline/useOnline";
import { readFailed, type ReadState } from "../lib/reads";

export function cx(...c: (string | false | null | undefined)[]): string {
  return c.filter(Boolean).join(" ");
}

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "sand";
const buttonStyles: Record<ButtonVariant, string> = {
  primary: "bg-primary text-on-primary hover:brightness-110 active:brightness-95 shadow-sm",
  secondary: "bg-surface text-ink border border-line hover:bg-sand/40",
  ghost: "bg-transparent text-primary hover:bg-primary/10",
  danger: "bg-danger text-white hover:brightness-110",
  sand: "bg-sand text-ink hover:brightness-95"
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; busy?: boolean; icon?: ReactNode; block?: boolean; size?: "md" | "lg" };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", busy, icon, block, size = "md", className, children, disabled, type = "button", ...rest }, ref
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition duration-150 ease-calm",
        "min-h-[44px] min-w-[44px] disabled:opacity-50 disabled:cursor-not-allowed select-none",
        size === "lg" ? "px-6 text-[17px] min-h-[52px]" : "px-4 text-[15px]",
        buttonStyles[variant], block && "w-full", className
      )}
      {...rest}
    >
      {busy ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : icon}
      {children}
    </button>
  );
});

export function ButtonLink({ variant = "primary", className, icon, children, ...rest }: LinkProps & { variant?: ButtonVariant; icon?: ReactNode }) {
  return (
    <Link
      className={cx("inline-flex items-center justify-center gap-2 rounded-xl font-medium px-4 min-h-[44px] text-[15px] transition duration-150 ease-calm", buttonStyles[variant], className)}
      {...rest}
    >
      {icon}
      {children}
    </Link>
  );
}

export function IconButton({ label, children, className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button type="button" aria-label={label} title={label}
      className={cx("inline-flex h-11 w-11 items-center justify-center rounded-full text-ink hover:bg-ink/5 transition duration-150", className)} {...rest}>
      {children}
    </button>
  );
}

type CardProps = { className?: string; children: ReactNode; as?: "section" | "div" | "article" | "li" } & Record<string, unknown>;
function CardWithRef(props: CardProps, ref: ForwardedRef<HTMLElement>) {
  const { className, children, as, ...rest } = props;
  const As = (as ?? "section") as "section";
  return <As ref={ref} className={cx("rounded-card bg-surface border border-line/70 shadow-[0_1px_2px_rgb(0_0_0/0.04)]", className)} {...rest}>{children}</As>;
}
/** A card; its element can be reached with `ref` (the card page gives its card the focus, Decision 96 #2). */
export const Card = forwardRef(CardWithRef as never) as unknown as (p: CardProps & { ref?: Ref<HTMLElement> }) => ReactElement;

const toneBg: Record<Tone, string> = {
  success: "bg-success/10 text-[rgb(var(--c-success-text))] border-success/25",
  warning: "bg-warning/15 text-[rgb(var(--c-warning-text))] border-warning/30",
  danger: "bg-danger/10 text-[rgb(var(--c-danger-text))] border-danger/25",
  info: "bg-info/10 text-[rgb(var(--c-info-text))] border-info/25",
  muted: "bg-ink/[0.06] text-muted border-ink/10",
  primary: "bg-primary/10 text-primary border-primary/25"
};
const toneDot: Record<Tone, string> = {
  success: "bg-success", warning: "bg-warning", danger: "bg-danger", info: "bg-info", muted: "bg-muted", primary: "bg-primary"
};

/**
 * A state chip: always words and colour, never colour alone (REQ-UX-04). A chip is one line (whitespace-nowrap) unless `wrap`
 * is set: a long label such as "Held: bank account not yet verified" is wider than a phone row, so a row that sits it under
 * its description (ED-RK-012) lets the words wrap inside the chip rather than run off the screen.
 */
export function Chip({ def, className, size = "md", wrap = false }: { def: ChipDef; className?: string; size?: "sm" | "md"; wrap?: boolean }) {
  return (
    <span data-chip={def.tone}
      className={cx("inline-flex gap-1.5 border font-medium", wrap ? "max-w-full items-start rounded-2xl text-left" : "items-center rounded-full whitespace-nowrap",
        size === "sm" ? "px-2 py-0.5 text-[12px]" : "px-2.5 py-1 text-[13px]", toneBg[def.tone], className)}>
      <span aria-hidden className={cx("h-1.5 w-1.5 shrink-0 rounded-full", wrap && "mt-[5px]", toneDot[def.tone])} />
      {def.label}
    </span>
  );
}

export function Money({ cents, className, sign }: { cents: number | null | undefined; className?: string; sign?: boolean }) {
  return <span className={cx("money tabular-nums", className)}>{formatRand(cents ?? null, { sign })}</span>;
}

// Decision 126 b: the app page this tab showed before this page load, if any (a link typed or pasted in the same tab, or
// a page opened afresh from another app page). Read once when the app loads; written as the page is left.
const LEFT_KEY = "rk-left-page";
let cameFrom: string | null = null;
if (typeof window !== "undefined") {
  try { cameFrom = window.sessionStorage.getItem(LEFT_KEY); window.sessionStorage.removeItem(LEFT_KEY); } catch { cameFrom = null; }
  window.addEventListener("pagehide", () => {
    try { window.sessionStorage.setItem(LEFT_KEY, window.location.pathname + window.location.search); } catch { /* storage unavailable: Back goes to the parent */ }
  });
}
/** Whether the entry before this page load is an app page: same-site referrer, or an app page left in this tab. */
function openedFromTheApp(): boolean {
  try {
    if (document.referrer) return new URL(document.referrer).origin === window.location.origin;
  } catch { return false; }
  return !!cameFrom && cameFrom !== window.location.pathname + window.location.search;
}

/** Decision 87 #6 and Decision 126 b: a clear back button. It goes back to the screen the person came from (the
 * browser's own previous entry, as iPhone swipe-back does), or to `to` when this screen was opened directly (a Teams or
 * email link, a new tab, or a tab whose only entry is this page: history.back() does nothing there), so a person is
 * never stuck on a page. */
export function BackButton({ to, label = "Back" }: { to: string; label?: string }) {
  const nav = useNavigate();
  return (
    <button type="button" onClick={() => {
      const idx = (typeof window !== "undefined" ? (window.history.state as { idx?: number } | null)?.idx : 0) ?? 0;
      if (idx > 0) nav(-1); else if (openedFromTheApp() && window.history.length > 1) window.history.back(); else nav(to);
    }} className="-ml-2 mb-1 inline-flex min-h-[44px] min-w-[44px] items-center gap-1 rounded-full px-2 text-[15px] font-medium text-primary hover:bg-primary/10">
      <ArrowLeft className="h-5 w-5" aria-hidden />{label}
    </button>
  );
}

export function PageHeader({ title, subtitle, actions, eyebrow, back }: { title: string; subtitle?: ReactNode; actions?: ReactNode; eyebrow?: ReactNode; back?: string }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3 pb-5">
      <div className="min-w-0">
        {back && <BackButton to={back} />}
        {eyebrow && <div className="text-[13px] font-medium uppercase tracking-wider text-muted mb-1">{eyebrow}</div>}
        <h1 className="text-[28px] leading-tight md:text-[32px] text-ink">{title}</h1>
        {subtitle && <p className="mt-1 text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

export function Section({ title, action, children, className }: { title?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx("mb-8", className)}>
      {(title || action) && (
        <div className="mb-3 flex items-center justify-between gap-2">
          {title && <h2 className="text-[20px] text-ink">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** Empty state: what happened, then the next step, one sentence each (REQ-UX-10). */
export function EmptyState({ what, next, action, icon }: { what: string; next: string; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div role="status" className="flex flex-col items-center text-center gap-2 rounded-card border border-dashed border-line px-6 py-10">
      {/* The Royal Kalahari nature scene in Dune (BD-79), with the screen's own icon below it when it has one. */}
      <NatureScene className="mb-1 aspect-[855/296] w-40" />
      {icon && <div className="text-muted mb-1" aria-hidden>{icon}</div>}
      <p className="font-medium text-ink">{what}</p>
      <p className="text-muted max-w-sm">{next}</p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/** A failed action: the server's one-sentence explanation, then the next step (REQ-UX-10). */
export function ErrorNote({ message, code, className }: { message: string; code?: string | undefined; className?: string | undefined }) {
  return (
    <div role="alert" className={cx("rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-[15px]", className)}>
      <p className="font-medium text-[rgb(var(--c-danger-text))]">{message}</p>
      <p className="text-ink/80">{nextStepFor(code)}</p>
    </div>
  );
}

/**
 * ED-RK-004: a read that failed, or was made without signal. It says so plainly, with a Try again button that reads again;
 * it is never an empty list or a zero (the screen's own empty wording is for a read that answered with nothing).
 * `what` is the thing not read, in lower case ("the payments on this invoice").
 */
export function ReadFailed({ what, onRetry, busy, className }: { what: string; onRetry: () => void; busy?: boolean; className?: string }) {
  const online = useOnline();
  return (
    <div data-testid="read-failed" className={cx("space-y-3", className)}>
      <ErrorNote message={readFailedMessage(what)} code={online ? "network" : "offline_only_online"} />
      <RetryButton what={what} onRetry={onRetry} busy={busy} />
    </div>
  );
}

/** The Try again button of a failed read: its name says what is read again. */
function RetryButton({ what, onRetry, busy, className }: { what: string; onRetry: () => void; busy?: boolean | undefined; className?: string }) {
  return <Button variant="secondary" busy={!!busy} className={className ?? ""} icon={<RefreshCw aria-hidden className="h-4 w-4" />} aria-label={readFailedRetryLabel(what)} onClick={onRetry}>{READ_FAILED_RETRY}</Button>;
}

/** ED-RK-004: a whole page whose main record could not be read: its title and Back, then the failed-read state (not "could not be found"). */
export function ReadFailedPage({ q, back, title, what }: { q: ReadState; back: string; title: string; what: string }) {
  return <><PageHeader back={back} title={title} /><ReadFailed what={what} busy={q.isFetching} onRetry={() => void q.refetch()} /></>;
}

/** ED-RK-004: the failed-read state of a read that sits beside its screen's other content (nothing when the read did not fail). */
export function ReadNote({ q, what }: { q: ReadState; what: string }) {
  return readFailed(q) ? <ReadFailed what={what} busy={q.isFetching} onRetry={() => void q.refetch()} className="mb-4" /> : null;
}

/**
 * ED-RK-004: the one place a screen decides between loading, a failed read and its answer. While the read is under way it
 * shows `loading`; when it failed it shows the failed-read state with Try again, which reads again; otherwise `children`
 * (the answer, including the screen's own honest empty wording). Children are built before the read has answered, so they
 * must not assume the data is there.
 */
export function Reading({ q, what, loading, children }: { q: ReadState; what: string; loading?: ReactNode; children: ReactNode }) {
  if (q.isLoading) return <>{loading ?? <Loading />}</>;
  if (readFailed(q)) return <ReadFailed what={what} busy={q.isFetching} onRetry={() => void q.refetch()} />;
  return <>{children}</>;
}

export function Notice({ children, tone = "info", className, role, "data-testid": testId }: { children: ReactNode; tone?: Tone; className?: string; role?: "status"; "data-testid"?: string }) {
  return <div data-testid={testId} role={role} className={cx("rounded-xl border px-4 py-3 text-[15px]", toneBg[tone], className)}>{children}</div>;
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cx("animate-pulse rounded-xl bg-ink/[0.07]", className)} />;
}

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="space-y-3 py-2">
      <div className="flex items-center gap-2 text-[15px] text-muted">
        <RkIcon on="background" className="h-5 w-8 animate-pulse motion-reduce:animate-none" />
        <span>{label}</span>
      </div>
      <Skeleton className="h-20" />
      <Skeleton className="h-20" />
      <Skeleton className="h-20 opacity-60" />
    </div>
  );
}

// Form fields ----------------------------------------------------------------------------
export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: string; error?: string; children: ReactNode; htmlFor: string }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-[14px] font-medium text-ink">{label}</label>
      {children}
      {hint && !error && <p className="text-[13px] text-muted">{hint}</p>}
      {error && <p id={`${htmlFor}-error`} className="text-[13px] text-danger">{error}</p>}
    </div>
  );
}

const inputClass = "block w-full min-w-0 rounded-xl border border-line bg-surface px-3.5 min-h-[48px] text-[16px] text-ink placeholder:text-muted/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25 transition";

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function TextInput({ className, ...rest }, ref) {
  return <input ref={ref} className={cx(inputClass, className)} {...rest} />;
});

/**
 * Decision 126 d (Decision 96 #3): a list's own search box. An <input type="search"> with a name, at 16 px so the
 * iPhone never zooms in on it (item h), and a short placeholder that fits the box on a phone; the full description is
 * its label. It shows whether or not the list has rows.
 */
export function ListSearch({ label, value, onChange, placeholder = "Search", onSubmit, className }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; onSubmit?: () => void; className?: string;
}) {
  return (
    <form role="search" aria-label={label} className={cx("mb-4 min-w-0", className)} onSubmit={(e) => { e.preventDefault(); onSubmit?.(); }}>
      <label className="relative block">
        <span className="sr-only">{label}</span>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
        <TextInput type="search" value={value} onChange={(e) => onChange(e.target.value)} className="pl-9" placeholder={placeholder} enterKeyHint="search" autoComplete="off" />
      </label>
    </form>
  );
}

export function TextArea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx(inputClass, "py-3 min-h-[96px]", className)} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx(inputClass, "appearance-none pr-9 bg-[length:16px] bg-[right_12px_center] bg-no-repeat", className)} {...rest}>{children}</select>;
}

export function Toggle({ checked, onChange, label, id }: { checked: boolean; onChange: (v: boolean) => void; label: string; id: string }) {
  return (
    <label htmlFor={id} className="flex min-h-[44px] items-center justify-between gap-3 cursor-pointer">
      <span className="text-[15px]">{label}</span>
      <span className="relative inline-flex">
        <input id={id} type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} className="peer sr-only" />
        <span className="h-7 w-12 rounded-full bg-ink/20 peer-checked:bg-primary transition" />
        <span className="absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
      </span>
    </label>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-xl bg-ink/[0.06] p-1 gap-1">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)}
          className={cx("min-h-[44px] rounded-lg px-3 text-[14px] font-medium transition", value === o.value ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink")}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** `wide`: the item takes both columns from sm up (LOT-WARN: a value with a warning under it is never squeezed into a narrow column). */
export function KeyValue({ items }: { items: { label: string; value: ReactNode; wide?: boolean }[] }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
      {items.map((i) => (
        <div key={i.label} className={cx("min-w-0", i.wide && "sm:col-span-2")}>
          <dt className="text-[13px] text-muted">{i.label}</dt>
          <dd className="text-[15px] text-ink break-words">{i.value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}
